import React, { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { Entity } from "@common/types/entity";
import { Transaction } from "@common/types/transaction";
import { Taxonomy } from "@common/categories";
import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import { currencyFormatter } from "@common/helpers/currency";
import { useStandardCategories } from "@hooks/useStandardCategories";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useClasses } from "@api/class.api";
import { useAnyEntityHasAccrualLedger } from "@api/ledger.api";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toDisplayDateOnly } from "@helpers/date";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    SpreadsheetGrid,
    ChangedCells,
} from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import {
    BaseCellProps,
    CategoryCell,
    CellType,
} from "@components/spreadsheet/cells";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { withRemoveLineColumn } from "@components/spreadsheet/removeLineColumn";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import { TransactionSplitClassCell } from "./TransactionSplitClassCell";
import { TransactionSplitCounterpartyCell } from "./TransactionSplitCounterpartyCell";
import { createLedgerColumn } from "./createLedgerColumn";
import { TransactionSplitPercentCell } from "./TransactionSplitPercentCell";
import { TransactionSplitScheduleCell } from "./TransactionSplitScheduleCell";
import {
    SplitRow,
    TransactionSplitFormRow,
    getGridTemplateColumns,
    amountToPercent,
    percentToAmount,
    areAmountsSymmetric,
    recalculateParentAmount,
    isPersonalEntityId,
    syncCategoryToEntity,
    syncEntityToCategory,
    syncRowPercentages,
    rowsToSplits,
} from "./transactionSplitUtils";
import styles from "./TransactionSplitSpreadsheet.module.scss";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
    transaction: Transaction;
    entities: Entity[];
}

export const TransactionSplitSpreadsheet: React.FC<Props> = ({
    transaction,
    entities,
}) => {
    const { values, setFieldValue } = useFormikContext<{
        splits: TransactionSplitFormRow[];
    }>();
    const isLocked = useIsDateLocked(
        transaction.entity.id,
        toDisplayDateOnly(transaction.dateOnly ?? transaction.date),
    );
    const standardCategories = useStandardCategories();
    const categoriesById = useCategoriesById();
    const { isActivated: hasClasses } = useClasses();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { schedules: hasSchedule } = useTypedFlags();
    const canRemoveSplits = !isLocked;
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const hasAccrualLedger = useAnyEntityHasAccrualLedger(entityIds);
    const gridTemplateColumns = getGridTemplateColumns({
        hasClasses,
        isGlFirst,
        hasSchedule,
        hasAccrualLedger,
    });

    const parentSplit = values.splits.find((s) => s.id === transaction.id);

    // -----------------------------------------------------------------------
    // Map Formik splits → SpreadsheetGrid rows
    // Private "_" fields (e.g. _id, _isParent) are not in any column def, so
    // SpreadsheetGrid preserves them unchanged on every mutation.
    // -----------------------------------------------------------------------
    const data: SpreadsheetData = useMemo(
        () =>
            values.splits.map((s) => ({
                _id: s.id,
                _isParent: s.id === transaction.id,
                splitDescription: s.splitDescription ?? "",
                amount: s.amount,
                percentage: amountToPercent(
                    s.amount ?? 0,
                    transaction.originalAmount,
                ),
                categoryId: s.categoryId,
                entityId: s.entityId,
                classIds: s.classIds ?? [],
                counterpartyId: s.counterpartyId ?? null,
                accountId: s.accountId ?? null,
                accrualAccountId: s.accrualAccountId ?? null,
                scheduleTemplateId: s.scheduleTemplateId ?? null,
            })),
        [values.splits, transaction.id, transaction.originalAmount],
    );

    const counterpartiesMap = useCounterpartyNameMap();

    // -----------------------------------------------------------------------
    // Column definitions
    // -----------------------------------------------------------------------
    const columns: ColumnDef[] = useMemo(() => {
        const createSplitLedgerColumn = (ledgerBasis: AccountingLedgerBasis) =>
            createLedgerColumn({
                ledgerBasis,
                getEntityId: (row) =>
                    typeof row.entityId === "number" ? row.entityId : undefined,
            });
        const ledgerColumns = [
            createSplitLedgerColumn(AccountingLedgerBasis.cash),
            ...(hasAccrualLedger
                ? [createSplitLedgerColumn(AccountingLedgerBasis.accruals)]
                : []),
        ];
        return [
            { field: "", label: "", cellType: CellType.ROW_NUMBER },
            {
                field: "splitDescription",
                label: "Description",
                cellType: CellType.STRING,
                placeholder: "Add description…",
            },
            ...(isGlFirst ? ledgerColumns : []),
            {
                field: "counterpartyId",
                label: "Counterparty",
                cellType: CellType.COUNTERPARTY,
                cellFn: (props) => (
                    <TransactionSplitCounterpartyCell
                        {...props}
                        value={props.value as string | null}
                        counterpartiesMap={counterpartiesMap}
                    />
                ),
            },
            ...(isGlFirst
                ? []
                : [
                      {
                          field: "categoryId",
                          label: "Category",
                          cellType: CellType.CATEGORY,
                          cellFn: (props: BaseCellProps) => (
                              <CategoryCell
                                  {...props}
                                  value={props.value as string | null}
                              />
                          ),
                      },
                  ]),
            ...(hasClasses
                ? [
                      {
                          field: "classIds",
                          label: "Class",
                          cellType: CellType.CLASS,
                          cellFn: (props: BaseCellProps) => (
                              <TransactionSplitClassCell
                                  {...props}
                                  value={props.value as string[] | null}
                              />
                          ),
                      },
                  ]
                : []),
            {
                field: "amount",
                label: "Amount",
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: (d: SpreadsheetData) => {
                    const total = d.reduce(
                        (sum, row) =>
                            sum +
                            fromMonetaryToInt((row.amount as number) ?? 0),
                        0,
                    );
                    return currencyFormatter.format(fromIntToMonetary(total));
                },
            },
            {
                field: "percentage",
                label: <span className={styles.percentHeader}>%</span>,
                cellType: CellType.MATH,
                cellFn: (props) => (
                    <TransactionSplitPercentCell
                        {...props}
                        value={props.value as number | null}
                    />
                ),
                aggregation: (d: SpreadsheetData) => {
                    const total = d.reduce(
                        (sum, row) =>
                            sum +
                            fromMonetaryToInt((row.amount as number) ?? 0),
                        0,
                    );
                    const originalTotal = fromMonetaryToInt(
                        transaction.originalAmount,
                    );
                    const pct =
                        originalTotal === 0
                            ? 0
                            : Math.round((total / originalTotal) * 10000) / 100;
                    return `${pct}%`;
                },
            },
            {
                field: "entityId",
                label: "Entity",
                cellType: CellType.ENTITY,
                entities,
            },
            ...(isGlFirst ? [] : ledgerColumns),
            ...(hasSchedule
                ? [
                      {
                          field: "scheduleTemplateId",
                          label: "Schedule",
                          cellType: CellType.STRING,
                          cellFn: (props: BaseCellProps) => (
                              <TransactionSplitScheduleCell
                                  {...props}
                                  value={
                                      typeof props.value === "string"
                                          ? props.value
                                          : null
                                  }
                              />
                          ),
                      },
                  ]
                : []),
        ];
    }, [
        entities,
        hasAccrualLedger,
        hasClasses,
        hasSchedule,
        isGlFirst,
        counterpartiesMap,
        transaction.originalAmount,
    ]);

    // -----------------------------------------------------------------------
    // Editability: row-number cells are always read-only (disables the
    // RowNumberCell context menu — "Add split" button is the sole insertion
    // mechanism). Parent row's amount is also read-only (auto-calculated).
    // -----------------------------------------------------------------------
    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number): boolean => {
            if (isLocked) {
                return false;
            }
            // Block row-number column for all rows — prevents
            // SpreadsheetGrid's built-in insert/remove row context menu
            if (colIdx === 0) {
                return false;
            }
            const row = data[rowIdx] as SplitRow | undefined;
            const columnField = columns[colIdx]?.field;
            if (
                row?._isParent &&
                (columnField === "amount" || columnField === "percentage")
            ) {
                // Block amount and % → parent row is auto-calculated
                return false;
            }
            if (
                (columnField === "accountId" ||
                    columnField === "accrualAccountId") &&
                isPersonalEntityId({ entityId: row?.entityId, entities })
            ) {
                return false;
            }
            return true;
        },
        [isLocked, data, columns, entities],
    );

    // -----------------------------------------------------------------------
    // Change handler: sync entity↔category, filter deleted rows, recalculate
    // the parent (remainder) row's amount, then write back to Formik.
    // -----------------------------------------------------------------------
    const handleChange = useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            let rows = newData.map((r) => ({ ...r })) as SplitRow[];

            // --- entity ↔ category sync; amount ↔ percentage sync ---
            for (const [rowIdx, colIdx] of changedCells) {
                const field = columns[colIdx]?.field;
                const row = rows[rowIdx];
                if (!row) {
                    continue;
                }
                if (field === "entityId") {
                    rows[rowIdx] = syncCategoryToEntity({
                        row,
                        entities,
                        personalCategoryId:
                            standardCategories[Taxonomy.personal].id,
                        categoriesById,
                    });
                } else if (field === "categoryId") {
                    rows[rowIdx] = syncEntityToCategory(
                        row,
                        entities,
                        categoriesById,
                    );
                } else if (field === "percentage") {
                    // User edited % → recalculate this row's amount
                    const pct = (row.percentage as number) ?? 0;
                    rows[rowIdx] = {
                        ...row,
                        amount: percentToAmount(
                            pct,
                            transaction.originalAmount,
                        ),
                    };
                } else if (field === "amount") {
                    // User edited amount → recalculate this row's %
                    rows[rowIdx] = {
                        ...row,
                        percentage: amountToPercent(
                            (row.amount as number) ?? 0,
                            transaction.originalAmount,
                        ),
                    };
                }
            }

            // --- filter rows cleared by SpreadsheetGrid's "Remove row" ---
            // SpreadsheetGrid sets removed rows to `{}`.  Any row with at
            // least one non-private, non-undefined field is considered live.
            // The parent row is always kept (identified by `_id`).
            rows = rows.filter(
                (r) =>
                    r._id !== undefined ||
                    r.splitDescription ||
                    r.amount !== undefined ||
                    r.categoryId ||
                    r.entityId !== undefined ||
                    r.classIds?.length ||
                    r.counterpartyId ||
                    r.accountId ||
                    r.accrualAccountId ||
                    r.scheduleTemplateId,
            );

            // --- recalculate parent (remainder) amount + percentage ---
            rows = recalculateParentAmount(
                rows,
                transaction.id,
                transaction.originalAmount,
            );
            rows = syncRowPercentages(rows, transaction.originalAmount);

            setFieldValue(
                "splits",
                rowsToSplits({ rows, parentSplit, entities }),
            );
        },
        [
            columns,
            entities,
            standardCategories,
            categoriesById,
            transaction.id,
            transaction.originalAmount,
            parentSplit,
            setFieldValue,
        ],
    );

    const grid = useMemo(
        () =>
            canRemoveSplits
                ? withRemoveLineColumn({
                      columns,
                      gridTemplateColumns,
                  })
                : { columns, gridTemplateColumns },
        [canRemoveSplits, columns, gridTemplateColumns],
    );

    // -----------------------------------------------------------------------
    // Add split (same logic as the previous TransactionSplitForm)
    // -----------------------------------------------------------------------
    const handleAddSplit = useCallback(() => {
        const currentSplits = values.splits;
        const totalCents = fromMonetaryToInt(transaction.originalAmount);
        const newCount = currentSplits.length + 1;

        let rows: SplitRow[];

        if (areAmountsSymmetric(currentSplits)) {
            const baseCents = Math.floor(totalCents / newCount);
            const baseAmount = fromIntToMonetary(baseCents);

            rows = [
                ...currentSplits.map((s) => ({
                    _id: s.id,
                    _isParent: s.id === transaction.id,
                    splitDescription: s.splitDescription,
                    amount: baseAmount,
                    percentage: amountToPercent(
                        baseAmount,
                        transaction.originalAmount,
                    ),
                    categoryId: s.categoryId,
                    entityId: s.entityId,
                    classIds: s.classIds ?? [],
                    counterpartyId: s.counterpartyId ?? null,
                    accountId: s.accountId ?? undefined,
                    accrualAccountId: s.accrualAccountId ?? undefined,
                    scheduleTemplateId: s.scheduleTemplateId ?? null,
                })),
                {
                    categoryId: parentSplit?.categoryId,
                    entityId: parentSplit?.entityId,
                    classIds: parentSplit?.classIds ?? [],
                    counterpartyId: parentSplit?.counterpartyId ?? null,
                    accountId: parentSplit?.accountId ?? undefined,
                    accrualAccountId:
                        parentSplit?.accrualAccountId ?? undefined,
                    amount: baseAmount,
                    percentage: amountToPercent(
                        baseAmount,
                        transaction.originalAmount,
                    ),
                },
            ];

            // Assign the leftover cent(s) to the parent row
            rows = recalculateParentAmount(
                rows,
                transaction.id,
                transaction.originalAmount,
            );
        } else {
            const newSplitCents = Math.floor(
                fromMonetaryToInt(parentSplit?.amount ?? 0) / 2,
            );
            const newSplitAmount = fromIntToMonetary(newSplitCents);

            rows = [
                ...currentSplits.map((s) => ({
                    _id: s.id,
                    _isParent: s.id === transaction.id,
                    splitDescription: s.splitDescription,
                    amount: s.amount,
                    percentage: amountToPercent(
                        s.amount ?? 0,
                        transaction.originalAmount,
                    ),
                    categoryId: s.categoryId,
                    entityId: s.entityId,
                    classIds: s.classIds ?? [],
                    counterpartyId: s.counterpartyId ?? null,
                    accountId: s.accountId ?? undefined,
                    accrualAccountId: s.accrualAccountId ?? undefined,
                    scheduleTemplateId: s.scheduleTemplateId ?? null,
                })),
                {
                    categoryId: parentSplit?.categoryId,
                    entityId: parentSplit?.entityId,
                    classIds: parentSplit?.classIds ?? [],
                    counterpartyId: parentSplit?.counterpartyId ?? null,
                    accountId: parentSplit?.accountId ?? undefined,
                    accrualAccountId:
                        parentSplit?.accrualAccountId ?? undefined,
                    amount: newSplitAmount,
                    percentage: amountToPercent(
                        newSplitAmount,
                        transaction.originalAmount,
                    ),
                },
            ];

            // Recalculate parent so total remains correct
            rows = recalculateParentAmount(
                rows,
                transaction.id,
                transaction.originalAmount,
            );
        }

        // Sync parent percentage after recalculation
        rows = syncRowPercentages(rows, transaction.originalAmount);

        setFieldValue("splits", rowsToSplits({ rows, parentSplit, entities }));
    }, [
        values.splits,
        entities,
        transaction.id,
        transaction.originalAmount,
        parentSplit,
        setFieldValue,
    ]);

    return (
        <div className={styles.wrapper}>
            <SpreadsheetGrid
                overflow="auto"
                data={data}
                columns={grid.columns}
                onChange={handleChange}
                isCellEditable={isCellEditable}
                showTotalsRow
                stickyHeader
                lightNotEditableCells
                showDropdownIndicator
                gridTemplateColumns={grid.gridTemplateColumns}
                className={styles.spreadsheetGrid}
            />
            <div className={styles.addButton}>
                <Button
                    disabled={isLocked}
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleAddSplit}
                    leftIcon={<PlusIcon />}
                >
                    Add split
                </Button>
            </div>
        </div>
    );
};
